/**
 * Bahadura Permanent RL / Temporary RL shared constants.
 * Mirrors civic-plan-backend/api/config/phaseConstants.js exactly.
 *
 * rl_application_type decides which journey an application takes:
 *  - PERMANENT: runs the plain original flow unmodified, no phase branching at all.
 *  - TEMPORARY: runs the original flow as Phase 1, then optionally the Phase 2
 *    continuation flow (current_phase / permanent_rl_status apply only here).
 *  - OFFLINE_TENTATIVE: the applicant already holds a Tentative RL certificate
 *    and Demand Note issued on paper, outside this system. There is nothing to
 *    assess or pay, so the application skips Level 1/2/3 Review, the Demand
 *    Note and the payment, and starts directly at the L1 -> L2 -> L3 Final
 *    Review chain, which ends in a Permanent RL certificate.
 */

/**
 * BEFORE - two application types:
 *
 * export const RL_APPLICATION_TYPE = {
 *   PERMANENT: "Permanent",
 *   TEMPORARY: "Temporary",
 * } as const;
 */
export const RL_APPLICATION_TYPE = {
  PERMANENT: "Permanent",
  TEMPORARY: "Temporary",
  OFFLINE_TENTATIVE: "Offline Tentative",
} as const;

export const PHASE = {
  TEMP_RL: "Phase_1",
  PERMANENT_RL: "Phase_2",
} as const;

export const PERMANENT_RL_STATUS = {
  DRAFT: "Draft",
  PENDING_L1: "Pending Level 1 Review",
  PENDING_L2: "Pending Level 2 Review",
  PENDING_L3: "Pending Level 3 Review",
  APPROVED: "Approved",
  RETURNED_L1: "Returned By Level 1 Reviewer",
  RETURNED_L2: "Returned By Level 2 Reviewer",
  RETURNED_L3: "Returned By Level 3 Reviewer",
  REJECTED_L2: "Rejected By Level 2 Reviewer",
  REJECTED_L3: "Rejected By Level 3 Reviewer",
} as const;

export const DOCUMENT_CONFIG_ID = {
  PERMANENT_RL_K_PRAT: 9991,
  PERMANENT_RL_REGISTRY_CERT: 9992,
  LAND_POSSESSION_CERTIFICATE: 9995,
  OWNERSHIP_DOCUMENT: 9996,
  LATEST_TAX_RECEIPT: 9997,
  COLOR_PHOTO_OF_PLOT: 9998,
  LS_SKETCH_MAP: 9999,
  ADDITIONAL_AFFIDAVIT: 10000,
  TENTATIVE_RL_COPY: 10001,
  SALE_DEED: 10002,
  /**
   * The applicant's signed copy of the downloaded Application Note PDF,
   * uploaded back as a document. Inserted with this fixed id on every DB so
   * code can refer to it (see APPLICATION_NOTE_EXCLUDED_DOCUMENT_IDS).
   */
  APPLICATION_FORM: 10003,
  /**
   * The Demand Note issued on paper with the offline Tentative RL, uploaded by
   * the applicant of an Offline Tentative application. Fixed id, same
   * convention as the rest of this list.
   */
  OFFLINE_DEMAND_NOTE: 10004,
  /**
   * The applicant's own Schematic Plan (PDF, JPG or PNG), optional, asked for
   * only on an Offline Tentative application. Reviewers can put it in the
   * certificate with "Use applicant's schematic". Fixed id, inserted by
   * drizzle/0028_offline_schematic_document.sql.
   */
  OFFLINE_SCHEMATIC_PLAN: 10005,
  /**
   * Documents of the main application form that had no name here because no
   * code referred to them by id until the display order below needed to.
   * Aadhar Card exists as two active rows in cp_document_config_admin.
   */
  AADHAR_CARD: 4,
  HAMIPATRA_KSHATIPURTI: 8,
  CURRENT_YEAR_7_12: 11,
  /** Asked for on the main application form; listed after the nine below. */
  LAYOUT_MAP: 13,
  AADHAR_CARD_ALTERNATE: 17,
} as const;

/**
 * The order documents are listed in, on the applicant's upload form and on
 * every reviewer / view screen. Without it the lists follow document id, which
 * put the Application Form last.
 *
 * The first nine are the main application's documents and Layout map closes
 * that set. After it come the documents of the Tentative -> Permanent
 * (Phase 2) request, which an Offline Tentative application also uploads, and
 * last the Offline Demand Note that only an Offline Tentative application has.
 *
 * A document not listed here keeps its place after the listed ones, in the
 * order it already had.
 */
export const DOCUMENT_DISPLAY_ORDER: readonly number[] = [
  DOCUMENT_CONFIG_ID.APPLICATION_FORM,
  DOCUMENT_CONFIG_ID.CURRENT_YEAR_7_12,
  DOCUMENT_CONFIG_ID.OWNERSHIP_DOCUMENT,
  DOCUMENT_CONFIG_ID.LATEST_TAX_RECEIPT,
  DOCUMENT_CONFIG_ID.HAMIPATRA_KSHATIPURTI,
  DOCUMENT_CONFIG_ID.AADHAR_CARD,
  DOCUMENT_CONFIG_ID.AADHAR_CARD_ALTERNATE,
  DOCUMENT_CONFIG_ID.COLOR_PHOTO_OF_PLOT,
  DOCUMENT_CONFIG_ID.LS_SKETCH_MAP,
  DOCUMENT_CONFIG_ID.ADDITIONAL_AFFIDAVIT,
  DOCUMENT_CONFIG_ID.LAYOUT_MAP,
  DOCUMENT_CONFIG_ID.PERMANENT_RL_K_PRAT,
  DOCUMENT_CONFIG_ID.PERMANENT_RL_REGISTRY_CERT,
  DOCUMENT_CONFIG_ID.TENTATIVE_RL_COPY,
  DOCUMENT_CONFIG_ID.SALE_DEED,
  DOCUMENT_CONFIG_ID.OFFLINE_DEMAND_NOTE,
  /** Optional, Offline Tentative only; listed after the offline Demand Note. */
  DOCUMENT_CONFIG_ID.OFFLINE_SCHEMATIC_PLAN,
];

/**
 * Returns the items in DOCUMENT_DISPLAY_ORDER. `getConfigId` reads an item's
 * document_config_id. The input is not changed, and items that share a
 * position (or are not listed) stay in their original relative order.
 */
export function sortByDocumentDisplayOrder<T>(
  items: readonly T[],
  getConfigId: (item: T) => number
): T[] {
  const rankOf = (item: T) => {
    const position = DOCUMENT_DISPLAY_ORDER.indexOf(Number(getConfigId(item)));
    return position === -1 ? DOCUMENT_DISPLAY_ORDER.length : position;
  };

  return items
    .map((item, index) => ({ item, index, rank: rankOf(item) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.item);
}

/**
 * The same ordering for an application's `documents` map, whose keys are the
 * document_config_id: pass Object.entries(application.documents).
 */
export function sortDocumentEntriesByDisplayOrder<T>(
  entries: [string, T][]
): [string, T][] {
  return sortByDocumentDisplayOrder(entries, ([configId]) => Number(configId));
}

/**
 * On the main application form (rl_application_type is a field on that same
 * form, current_phase is always Phase_1 there - Phase_2 only exists after
 * this form has already been submitted and approved once), these documents
 * should only appear when Application Type = Permanent. Kept as one list
 * instead of individual id checks so adding another Permanent-only document
 * later means adding one id here, not touching every filter/validator that
 * checks it.
 */
/**
 * BEFORE — Ownership Document, Latest Tax Receipt, Color Photo of Plot, L.S.
 * Sketch Map and Additional Affidavit were Permanent-only:
 *
 * export const PERMANENT_ONLY_DOCUMENT_IDS = [
 *   DOCUMENT_CONFIG_ID.PERMANENT_RL_K_PRAT,
 *   DOCUMENT_CONFIG_ID.PERMANENT_RL_REGISTRY_CERT,
 *   DOCUMENT_CONFIG_ID.LAND_POSSESSION_CERTIFICATE,
 *   DOCUMENT_CONFIG_ID.OWNERSHIP_DOCUMENT,
 *   DOCUMENT_CONFIG_ID.LATEST_TAX_RECEIPT,
 *   DOCUMENT_CONFIG_ID.COLOR_PHOTO_OF_PLOT,
 *   DOCUMENT_CONFIG_ID.LS_SKETCH_MAP,
 *   DOCUMENT_CONFIG_ID.ADDITIONAL_AFFIDAVIT,
 * ] as const;
 *
 * Tentative Phase I and Permanent now ask for the same document list, so those
 * five are shown to both. What remains here is not mapped to the main form at
 * all — K Prat and Registry Certificate reach applicants only through the
 * Phase 2 continuation modal — so this list no longer hides anything on it.
 */
export const PERMANENT_ONLY_DOCUMENT_IDS = [
  DOCUMENT_CONFIG_ID.PERMANENT_RL_K_PRAT,
  DOCUMENT_CONFIG_ID.PERMANENT_RL_REGISTRY_CERT,
  DOCUMENT_CONFIG_ID.LAND_POSSESSION_CERTIFICATE,
] as const;

/**
 * Documents the main application form asks for only when Application Type =
 * Offline Tentative: the documents the Tentative -> Permanent (Phase 2)
 * request asks for, plus the offline Demand Note. They are mapped to the
 * Documents form like any other document, and this list is what keeps them
 * hidden for Permanent and Tentative applications. Mirrors the backend list
 * of the same name, which skips them in the mandatory-document validator.
 *
 * Registry Certificate is also in PERMANENT_ONLY_DOCUMENT_IDS above; filters
 * have to check this list first, otherwise a plain Permanent application
 * would start being asked for it the moment it is mapped to the form.
 */
export const OFFLINE_TENTATIVE_ONLY_DOCUMENT_IDS: readonly number[] = [
  DOCUMENT_CONFIG_ID.PERMANENT_RL_REGISTRY_CERT,
  DOCUMENT_CONFIG_ID.TENTATIVE_RL_COPY,
  DOCUMENT_CONFIG_ID.SALE_DEED,
  DOCUMENT_CONFIG_ID.OFFLINE_DEMAND_NOTE,
  /** Optional; this list keeps it off the Tentative and Permanent forms. */
  DOCUMENT_CONFIG_ID.OFFLINE_SCHEMATIC_PLAN,
];

/**
 * Documents left out of the printed Application Note's own checklist. The
 * signed Application Form is uploaded *after* downloading that PDF, so listing
 * it inside the very form being signed would be circular.
 */
export const APPLICATION_NOTE_EXCLUDED_DOCUMENT_IDS: readonly number[] = [
  DOCUMENT_CONFIG_ID.APPLICATION_FORM,
];

/**
 * The document_Type value the two Tentative-RL-continuation documents above
 * were created under in cp_document_config_admin. They're real, admin-owned
 * rows (unlike K Prat/Registry Certificate below), but deliberately not
 * mapped to any form/step, since they only ever get uploaded through
 * PermanentRLUploadModal - so a plain `docForm.documentConfigs.find(...)`
 * lookup won't find them either, same fallback need as the sentinel pair.
 */
export const PERMANENT_RL_CONTINUATION_DOCUMENT_TYPE =
  "Permanent RL Continuation Document";

/**
 * Fixed display labels for the Permanent RL Phase 2 continuation documents
 * (uploaded via PermanentRLUploadModal), keyed by document_config_id above.
 * K Prat/Registry Certificate are sentinel IDs with no real config row;
 * Tentative RL Copy/Sale Deed are real cp_document_config_admin rows that
 * just aren't mapped to any form - either way, a plain
 * `docForm.documentConfigs.find(...)` lookup never matches them.
 */
export const PERMANENT_RL_DOCUMENT_LABELS: Record<number, string> = {
  [DOCUMENT_CONFIG_ID.PERMANENT_RL_K_PRAT]: "K Prat Document",
  [DOCUMENT_CONFIG_ID.PERMANENT_RL_REGISTRY_CERT]: "Registry Certificate",
  [DOCUMENT_CONFIG_ID.TENTATIVE_RL_COPY]: "Tentative RL Copy",
  [DOCUMENT_CONFIG_ID.SALE_DEED]: "Sale Deed",
};

/**
 * WHAT: Single shared lookup for a document's display label in every
 * "Documents" table across the app (L1/L2/L3 review + payment-confirm
 * screens, view-application-details for both applicant and admin).
 * WHY: User instruction (2026-09-24) — the Permanent RL Phase 2 K Prat
 * Document / Registry Certificate (document_config_id 9991/9992) were
 * uploaded successfully but every one of those 8 screens independently
 * re-implemented the same `docForm.documentConfigs.find(...)` lookup with
 * no fallback for these two sentinel IDs, so they were silently filtered
 * out everywhere ("No Documents" shown despite a successful upload).
 * Centralizing here means any future screen reuses this instead of
 * duplicating (and re-missing) the same lookup.
 * HOW: tries the real form-configured document label first (regular
 * documents, including ones re-submitted in Phase 2 under the same
 * config id), then the form's disabled documents, then falls back to the
 * fixed Permanent RL label map above.
 * Returns null (caller should filter the row out) if none matches.
 */
/**
 * BEFORE — docForm accepted active documents only:
 *
 *   | { documentConfigs?: Array<{ document_config_id: number; document_label?: string }> }
 */
export function resolveDocumentLabel(
  docForm:
    | {
        documentConfigs?: Array<{ document_config_id: number; document_label?: string }>;
        disabledDocumentConfigs?: Array<{ document_config_id: number; document_label?: string }>;
      }
    | null
    | undefined,
  configId: number
): string | null {
  const configuredLabel = docForm?.documentConfigs?.find(
    (e) => e.document_config_id === configId
  )?.document_label;

  if (configuredLabel) return configuredLabel;

  /**
   * A document switched off in Config Admin drops out of documentConfigs, but
   * uploads made before that still belong on the reviewer's table. Without
   * this, disabling e.g. Layout map would silently hide every existing Layout
   * map upload across all 8 document tables.
   */
  const disabledLabel = docForm?.disabledDocumentConfigs?.find(
    (e) => e.document_config_id === configId
  )?.document_label;

  if (disabledLabel) return disabledLabel;

  return PERMANENT_RL_DOCUMENT_LABELS[configId] || null;
}

/**
 * The "Application Type" field (uid rl_application_type) is a DB-driven
 * `configurable-select` field (cp_form_fields / cp_field_options), same as
 * road_leading_to_land. Its raw react-hook-form value is therefore the
 * numeric cp_field_options.id of the chosen option (see RenderSelect's
 * `valueAsNumber: true`), NOT the "Permanent"/"Temporary" string — that
 * string conversion only happens server-side in transformApplication().
 * These option IDs are inserted explicitly in the DB migration so both
 * sides can agree on fixed values, same convention as the 9991/9992
 * document_config_id's above.
 */
/**
 * BEFORE - two options:
 *
 * export const RL_APPLICATION_TYPE_OPTION_ID = {
 *   PERMANENT: 9993,
 *   TEMPORARY: 9994,
 * } as const;
 */
export const RL_APPLICATION_TYPE_OPTION_ID = {
  PERMANENT: 9993,
  TEMPORARY: 9994,
  OFFLINE_TENTATIVE: 9990,
} as const;

/**
 * True only for a Temporary-type application currently in its Phase 2
 * (post-completion Permanent RL) continuation. A Permanent-type application
 * never satisfies this, regardless of current_phase.
 */
export function isPhase2Continuation(application: any): boolean {
  return (
    application?.rl_application_type === RL_APPLICATION_TYPE.TEMPORARY &&
    application?.current_phase === PHASE.PERMANENT_RL
  );
}

/**
 * The part of an application the document tables need to tell which phase a
 * document belongs to.
 */
type DocumentPhaseApplication =
  | { rl_application_type?: unknown; current_phase?: unknown }
  | Record<string, unknown>
  | null
  | undefined;

/**
 * True when the document was uploaded in Phase 2 of a Tentative application.
 * `documentPhase` is the `phase` stored on the uploaded document itself
 * (cp_documents.phase), which is "Phase_2" for the documents of the
 * Tentative -> Permanent request and for a Phase 1 document re-uploaded then.
 */
function isPhase2Document(documentPhase: unknown): boolean {
  return documentPhase === PHASE.PERMANENT_RL;
}

/**
 * A document's name as the reviewer and view tables show it. Only a Tentative
 * application runs in two phases, so only its documents say which one they
 * were submitted in: "(Tentative)" for Phase 1 and "(Permanent)" for Phase 2.
 * Permanent and Offline Tentative applications keep the plain name.
 */
export function getDocumentLabelWithPhase(
  label: string,
  application: DocumentPhaseApplication,
  documentPhase: unknown
): string {
  const applicationType = (application as { rl_application_type?: unknown } | null | undefined)
    ?.rl_application_type;

  if (applicationType !== RL_APPLICATION_TYPE.TEMPORARY) return label;

  /**
   * The "(Tentative)" / "(Permanent)" tag is switched off: every document
   * table shows the document name only. Kept here for when it is needed
   * again; restore the return below and remove `void documentPhase;`.
   *
   * return isPhase2Document(documentPhase)
   *   ? `${label} (Permanent)`
   *   : `${label} (Tentative)`;
   */
  void documentPhase;
  return label;
}

/**
 * True for a Phase 1 document of a Tentative application that has moved on to
 * Phase 2. Those documents were accepted when the Tentative RL was issued, so
 * a Phase 2 reviewer can see them but can no longer accept or reject them;
 * only the documents submitted with the Phase 2 request stay open.
 */
export function isDocumentFromEarlierPhase(
  application: DocumentPhaseApplication,
  documentPhase: unknown
): boolean {
  return isPhase2Continuation(application) && !isPhase2Document(documentPhase);
}

/**
 * True when a raw "Application Type" value means Offline Tentative. The value
 * comes in two shapes: the dropdown's option id (number or string) while the
 * form is being filled, and the stored RL_APPLICATION_TYPE string on a saved
 * application. Mirrors the backend helper of the same name.
 */
export function isOfflineTentativeTypeValue(value: unknown): boolean {
  const normalized = String(value ?? "").trim().toLowerCase();
  return (
    normalized === String(RL_APPLICATION_TYPE_OPTION_ID.OFFLINE_TENTATIVE) ||
    normalized === RL_APPLICATION_TYPE.OFFLINE_TENTATIVE.toLowerCase()
  );
}

/**
 * The Application Type option id for a type as cp_applications stores it
 * ("Permanent", "Temporary", "Offline Tentative"), or null for anything else.
 *
 * A saved application holds the type as that string, while the dropdown's
 * options are the fixed ids 9993 / 9994 / 9990 named "Permanent RL",
 * "Tentative RL" and "Offline Tentative RL". "Temporary" matches neither the
 * id nor the name of "Tentative RL", so without this a Tentative application
 * opened in a form (for example the reviewers' Edit Application) showed
 * "Select Application Type".
 */
export function rlApplicationTypeOptionId(value: unknown): number | null {
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === RL_APPLICATION_TYPE.PERMANENT.toLowerCase()) {
    return RL_APPLICATION_TYPE_OPTION_ID.PERMANENT;
  }
  if (normalized === RL_APPLICATION_TYPE.TEMPORARY.toLowerCase()) {
    return RL_APPLICATION_TYPE_OPTION_ID.TEMPORARY;
  }
  if (normalized === RL_APPLICATION_TYPE.OFFLINE_TENTATIVE.toLowerCase()) {
    return RL_APPLICATION_TYPE_OPTION_ID.OFFLINE_TENTATIVE;
  }
  return null;
}

/**
 * True for an application stored as Offline Tentative.
 */
/**
 * BEFORE - accepted only objects that declare rl_application_type:
 *
 * export function isOfflineTentative(
 *   application: { rl_application_type?: unknown } | null | undefined
 * ): boolean {
 *
 * The Level 2 and Level 3 application types do not declare that field (they
 * carry it only through their Record<string, unknown> part), so TypeScript
 * rejected them. A plain record is accepted as well.
 */
export function isOfflineTentative(
  application:
    | { rl_application_type?: unknown }
    | Record<string, unknown>
    | null
    | undefined
): boolean {
  return (
    (application as { rl_application_type?: unknown } | null | undefined)
      ?.rl_application_type === RL_APPLICATION_TYPE.OFFLINE_TENTATIVE
  );
}

/**
 * Names (and cp_applications columns) of the four optional reference details
 * an Offline Tentative applicant can give about the paper Demand Note and
 * Tentative RL certificate they already hold. They are not cp_form_fields
 * rows: they belong to one application type only, so they are rendered by
 * OfflineTentativeDetails instead of the generic field loop, which would
 * otherwise show them as empty rows on every other application.
 */
export const OFFLINE_TENTATIVE_DETAIL_FIELDS = [
  {
    name: "offline_demand_note_no",
    type: "text",
    label: "Offline Demand Note No.",
    labelHindi: "ऑफलाइन डिमांड नोट क्रमांक",
    labelMarathi: "ऑफलाइन डिमांड नोट क्रमांक",
  },
  {
    name: "offline_demand_note_date",
    type: "date",
    label: "Offline Demand Note Date",
    labelHindi: "ऑफलाइन डिमांड नोट दिनांक",
    labelMarathi: "ऑफलाइन डिमांड नोट दिनांक",
  },
  {
    name: "offline_certificate_no",
    type: "text",
    label: "Offline Tentative RL Certificate No.",
    labelHindi: "ऑफलाइन अस्थायी आरएल प्रमाणपत्र क्रमांक",
    labelMarathi: "ऑफलाइन तात्पुरते आरएल प्रमाणपत्र क्रमांक",
  },
  {
    name: "offline_certificate_date",
    type: "date",
    label: "Offline Tentative RL Certificate Date",
    labelHindi: "ऑफलाइन अस्थायी आरएल प्रमाणपत्र दिनांक",
    labelMarathi: "ऑफलाइन तात्पुरते आरएल प्रमाणपत्र दिनांक",
  },
] as const;

/**
 * "Permanent"/"Tentative" display label for the navbar Application Type
 * field. The Temporary type reads as "Tentative" here even though the
 * underlying RL_APPLICATION_TYPE.TEMPORARY constant and the stored
 * rl_application_type value both stay "Temporary" - only the label users
 * actually see was renamed.
 */
export function getApplicationTypeLabel(rlApplicationType?: unknown): string {
  if (rlApplicationType === RL_APPLICATION_TYPE.PERMANENT) return "Permanent";
  if (rlApplicationType === RL_APPLICATION_TYPE.TEMPORARY) return "Tentative";
  /**
   * BEFORE - any other value, including a stored "Offline Tentative", fell
   * through to "Nil".
   */
  if (rlApplicationType === RL_APPLICATION_TYPE.OFFLINE_TENTATIVE) return "Offline Tentative";
  return "Nil";
}

/**
 * "Phase I"/"Phase II" display label for the navbar Application Phase field.
 */
export function getApplicationPhaseLabel(currentPhase?: unknown): string {
  if (currentPhase === PHASE.PERMANENT_RL) return "Phase II";
  if (currentPhase === PHASE.TEMP_RL) return "Phase I";
  return "Nil";
}

/**
 * Combined "Permanent" / "Tentative (Phase I)" / "Tentative (Phase II)"
 * display label for the Application Type column on application-listing
 * tables. Same renaming as getApplicationTypeLabel above - Temporary shows
 * as "Tentative" to the user, the stored value and constant name are
 * untouched.
 */
export function getApplicationTypeColumnLabel(
  application:
    | { rl_application_type?: unknown; current_phase?: unknown }
    | null
    | undefined
): string {
  if (!application) return "NA";
  if (application.rl_application_type === RL_APPLICATION_TYPE.PERMANENT) {
    return "Permanent";
  }
  /**
   * A Tentative application in its Phase II continuation is being converted
   * to Permanent, so the tables show it as Permanent, as the Navbar does.
   * Display only: rl_application_type stays "Temporary" in the database.
   * BEFORE:
   * return application.current_phase === PHASE.PERMANENT_RL
   *   ? "Tentative"
   *   : "Tentative";
   */
  if (application.rl_application_type === RL_APPLICATION_TYPE.TEMPORARY) {
    return application.current_phase === PHASE.PERMANENT_RL
      ? "Permanent"
      : "Tentative";
  }
  /**
   * BEFORE - any other value, including a stored "Offline Tentative", fell
   * through to "NA".
   */
  if (application.rl_application_type === RL_APPLICATION_TYPE.OFFLINE_TENTATIVE) {
    return "Offline Tentative";
  }
  return "NA";
}

/**
 * Plot-detail / land-possession / application-type fields that must always
 * render on the L1/L2/L3 review screens' <ApplicationList /> regardless of
 * whether `selectedFields` resolved (it can come up empty if service-id to
 * service-name resolution fails), matching the existing force-include
 * precedent already used there for typeOfApplicationSubmitterBy/submittedBy.
 */
export const PLOT_AND_LAYOUT_FIELD_UIDS = [
  "rl_application_type",
  "unauthorized_layout_name",
  "area_as_per_7_12",
  "area_as_per_revenue_record",
  "area_as_per_sale_deed",
  "area_under_possession",
  "road_leading_to_land",
  "plot_usage_dp",
  "address",
] as const;
